import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAIAssistantContext } from "@/components/ai-assistant/context";
import { cn } from "@/utils/cn";
import { MessageSquareDashed, X } from "lucide-react";
import { CanvasActionButton } from "./canvas-action-button";

/**
 * Temporary-chat switch.
 *
 * It sits with the other canvas controls rather than in the history rail
 * because it is a property of the session you are in, not a list action — and
 * because a temporary chat, by definition, never appears in that list.
 *
 * Off: the shared icon button, quiet and identical to New chat. On: a labelled
 * pill with an explicit exit, so the mode can never be active without saying so.
 */
export const TempChatToggle = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation(["common"]);
  const { isTempChat, startTempChat, stopTempChat } = useAIAssistantContext();

  if (isTempChat)
    return (
      <Button
        size="sm"
        variant="outline-primary"
        aria-pressed
        className={cn(
          "surface-frosted gap-1.5 rounded-full px-3.5 text-xs shadow-sm",
          compact ? "h-8" : "h-10",
        )}
        onClick={stopTempChat}
      >
        <MessageSquareDashed size={compact ? 14 : 18} />
        {t("common:ai_assistant.temporary")}
        <X size={14} className="opacity-70" />
      </Button>
    );

  return (
    <CanvasActionButton
      icon={MessageSquareDashed}
      label={t("common:ai_assistant.temporary_chat")}
      hint={t("common:ai_assistant.temporary_chat_not_saved")}
      onClick={startTempChat}
      compact={compact}
    />
  );
};
